import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserRole } from "@/hooks/use-user-role";
import { Settings as SettingsIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  userSettingsSections,
  orgSettingsSections,
  useSettingsData,
  ProfileSection,
  CreditsSection,
  MembersSection,
  RoomSettingsSection,
} from "./settings-sections";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

type SettingsSection = "profile" | "credits" | "members" | "room";

function normalizeSettingsSection(
  section: string | null | undefined,
): SettingsSection {
  switch (section) {
    case "credits":
    case "members":
    case "room":
      return section;
    default:
      return "profile";
  }
}

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const legacyTabParam = searchParams.get("tab");
  const activeSection = normalizeSettingsSection(sectionParam || legacyTabParam);
  const { isAdmin, isOwner } = useUserRole();

  const settingsData = useSettingsData();

  useEffect(() => {
    if (sectionParam || !legacyTabParam) {
      return;
    }

    const normalized = normalizeSettingsSection(legacyTabParam);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("section", normalized);
    nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true });
  }, [legacyTabParam, searchParams, sectionParam, setSearchParams]);

  // Check for critical errors
  const hasCriticalErrors = useHasCriticalError(settingsData.queryErrors);

  const setActiveSection = (section: SettingsSection) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("section", section);
    nextParams.delete("tab");
    setSearchParams(nextParams);
  };

  // Show blocking error screen when critical APIs fail
  if (hasCriticalErrors) {
    return (
      <ApiErrorScreen 
        errors={settingsData.queryErrors}
        context="Dashboard - Settings"
      />
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return (
          <ProfileSection
            currentUser={settingsData.currentUser}
            userLoading={settingsData.userLoading}
            nameValue={settingsData.nameValue}
            setNameValue={settingsData.setNameValue}
            phoneValue={settingsData.phoneValue}
            setPhoneValue={settingsData.setPhoneValue}
            updatingProfile={settingsData.updatingProfile}
            hasProfileChanges={settingsData.hasProfileChanges}
            handleUpdateProfile={settingsData.handleUpdateProfile}
            isOwner={isOwner}
            userOrganizations={settingsData.userOrganizations}
            currentOrganizationId={settingsData.organizationId}
            handleLeaveOrganization={settingsData.handleLeaveOrganization}
          />
        );
      case "credits":
        return (
          <CreditsSection
            organizationId={settingsData.organizationId}
            isAdmin={isAdmin}
            subscription={settingsData.subscription}
            creditsBreakdown={settingsData.creditsBreakdown}
            planConfigs={settingsData.planConfigs}
            billingCapabilities={settingsData.billingCapabilities}
            creditsLoading={settingsData.creditsLoading}
            transactions={settingsData.transactions}
            transactionsLoading={settingsData.transactionsLoading}
            transactionsPageInfo={settingsData.transactionsPageInfo}
            loadingMoreTransactions={settingsData.loadingMoreTransactions}
            handleLoadMoreTransactions={settingsData.handleLoadMoreTransactions}
            showTopUp={settingsData.showTopUp}
            setShowTopUp={settingsData.setShowTopUp}
            handleTopUpComplete={settingsData.handleTopUpComplete}
            billingContactName={settingsData.billingContactName}
            setBillingContactName={settingsData.setBillingContactName}
            billingContactEmail={settingsData.billingContactEmail}
            setBillingContactEmail={settingsData.setBillingContactEmail}
            updatingBillingContact={settingsData.updatingSettings}
            hasBillingContactChanges={settingsData.hasBillingContactChanges}
            handleSaveBillingContact={settingsData.handleSaveBillingContact}
          />
        );
      case "members":
        return (
          <MembersSection
            currentUser={settingsData.currentUser}
            organizationName={settingsData.organization?.name || "Your Organization"}
            members={settingsData.members}
            orgLoading={settingsData.orgLoading}
            membersPageInfo={settingsData.membersPageInfo}
            loadingMoreMembers={settingsData.loadingMoreMembers}
            handleLoadMoreMembers={settingsData.handleLoadMoreMembers}
            isAdmin={isAdmin}
            inviteEmail={settingsData.inviteEmail}
            setInviteEmail={settingsData.setInviteEmail}
            invitingMember={settingsData.invitingMember}
            handleInviteMember={settingsData.handleInviteMember}
            handleKickMember={settingsData.handleKickMember}
            handleResendInvitation={settingsData.handleResendInvitation}
            handleRevokeInvitation={settingsData.handleRevokeInvitation}
          />
        );
      case "room":
        return (
          <RoomSettingsSection
            isAdmin={isAdmin}
            maxEmptyTimeout={settingsData.maxEmptyTimeout}
            setMaxEmptyTimeout={settingsData.setMaxEmptyTimeout}
            noModeratorTimeout={settingsData.noModeratorTimeout}
            setNoModeratorTimeout={settingsData.setNoModeratorTimeout}
            updatingSettings={settingsData.updatingSettings}
            hasRoomSettingsChanges={settingsData.hasRoomSettingsChanges}
            handleSaveSettings={settingsData.handleSaveSettings}
            brandName={settingsData.brandName}
            setBrandName={settingsData.setBrandName}
            accentColor={settingsData.accentColor}
            setAccentColor={settingsData.setAccentColor}
            preJoinBgColor={settingsData.preJoinBgColor}
            setPreJoinBgColor={settingsData.setPreJoinBgColor}
            welcomeMessage={settingsData.welcomeMessage}
            setWelcomeMessage={settingsData.setWelcomeMessage}
            redirectUrl={settingsData.redirectUrl}
            setRedirectUrl={settingsData.setRedirectUrl}
            logoUrl={settingsData.logoUrl}
            uploadingLogo={settingsData.uploadingLogo}
            handleLogoUpload={settingsData.handleLogoUpload}
            handleLogoRemove={settingsData.handleLogoRemove}
          />
        );
      default:
        return (
          <ProfileSection
            currentUser={settingsData.currentUser}
            userLoading={settingsData.userLoading}
            nameValue={settingsData.nameValue}
            setNameValue={settingsData.setNameValue}
            phoneValue={settingsData.phoneValue}
            setPhoneValue={settingsData.setPhoneValue}
            updatingProfile={settingsData.updatingProfile}
            hasProfileChanges={settingsData.hasProfileChanges}
            handleUpdateProfile={settingsData.handleUpdateProfile}
            isOwner={isOwner}
            userOrganizations={settingsData.userOrganizations}
            currentOrganizationId={settingsData.organizationId}
            handleLeaveOrganization={settingsData.handleLeaveOrganization}
          />
        );
    }
  };

  const allSections = [
    { title: "Personal", items: userSettingsSections },
    { title: settingsData.organization?.name || "Organization", items: orgSettingsSections },
  ];

  return (
    <div className="flex h-full">
      {/* Flowbite-style Settings Sidebar */}
      <aside className="w-64 h-full border-r border-border/40 bg-card/60 backdrop-blur-md shrink-0">
        <div className="h-full flex flex-col overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-3 px-3 py-4 border-b border-border/30">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/20 text-secondary shrink-0">
              <SettingsIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-base font-bold text-foreground truncate">Settings</h1>
              <p className="font-body text-xs text-muted-foreground truncate">Manage your account</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4">
            {allSections.map((section) => (
              <div key={section.title} className="mb-6">
                <p className="px-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {section.title}
                </p>
                <ul className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = activeSection === item.id;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => setActiveSection(item.id)}
                          className={cn(
                            "flex items-center w-full rounded-lg px-3 py-2.5 transition-colors duration-150 relative text-left",
                            isActive
                              ? "bg-secondary/20 text-secondary font-medium"
                              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          )}
                        >
                          {/* Active indicator bar */}
                          {isActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-secondary rounded-r-full" />
                          )}
                          <item.icon className={cn(
                            "h-5 w-5 shrink-0 transition-colors duration-150",
                            isActive ? "text-secondary" : ""
                          )} />
                          <span className="ms-3 whitespace-nowrap text-base font-medium">{item.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        <div className="max-w-5xl mx-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
